import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw, ShieldCheck, X } from 'lucide-react';
import api from '../services/api';

const GROUPS = [
  ['dashboard', 'Dashboard'], ['patients', 'Cadastro de pacientes'], ['appointments', 'Agenda'],
  ['clinical_records', 'Prontuário clínico'], ['aligners', 'Alinhadores'],
  ['anamnese', 'Anamnese'], ['images', 'Imagens'],
  ['documents', 'Documentos clínicos'], ['contracts', 'Contratos'], ['whatsapp', 'WhatsApp'],
  ['compliance', 'Boas Práticas'],
  ['inventory', 'Estoque'], ['billing', 'Financeiro'], ['ai', 'Inteligência artificial'],
  ['settings', 'Configurações'], ['subscription', 'Assinatura'], ['audit', 'Auditoria'],
];

// Permanecem no catálogo interno por compatibilidade, mas só devem ser
// configuráveis quando possuírem proteção efetiva de ponta a ponta.
const HIDDEN_PERMISSIONS = new Set([
  'patients.export', 'patients.view_sensitive', 'patients.update_sensitive',
  'clinical_records.create', 'clinical_records.sign', 'clinical_records.inactivate',
  'images.download', 'whatsapp.manage',
  'ai.administrative_use', 'ai.financial_use',
]);

const PERMISSION_LABELS = {
  'patients.view': 'Acessar lista e cadastro de pacientes',
  'patients.create': 'Cadastrar pacientes',
  'patients.update': 'Editar dados cadastrais',
  'patients.inactivate': 'Inativar ou excluir pacientes',
  'clinical_records.view': 'Visualizar conteúdo do prontuário',
  'clinical_records.update': 'Editar conteúdo do prontuário',
  'clinical_records.export': 'Exportar prontuário completo',
  'aligners.view': 'Visualizar a Central de Alinhadores',
  'aligners.manage': 'Cadastrar e editar alinhadores',
  'compliance.view': 'Visualizar Boas Práticas',
  'compliance.manage': 'Gerenciar Boas Práticas',
};

const PERMISSION_DESCRIPTIONS = {
  'patients.view': 'Exibe a lista e os dados administrativos. Não libera odontograma, tratamentos ou evolução clínica.',
  'clinical_records.view': 'Libera odontograma, tratamentos e evolução clínica. A lista exige também acesso ao cadastro de pacientes.',
  'clinical_records.update': 'Permite registrar e alterar informações clínicas nas áreas liberadas.',
  'aligners.view': 'Controla o menu, a aba e a leitura dos tratamentos com alinhadores.',
  'aligners.manage': 'Permite criar, alterar e registrar eventos de alinhadores.',
  'compliance.view': 'Libera o menu e a consulta dos planos, documentos e registros do módulo.',
  'compliance.manage': 'Permite enviar documentos, atualizar o plano e registrar ciclos e providências.',
};

const ACTION_LABELS = {
  view: 'Visualizar', create: 'Cadastrar', update: 'Editar', inactivate: 'Inativar',
  export: 'Exportar', view_sensitive: 'Visualizar dados sensíveis',
  update_sensitive: 'Editar dados sensíveis', cancel: 'Cancelar', delete: 'Excluir',
  send_confirmation: 'Enviar confirmações', sign: 'Assinar', manage: 'Gerenciar',
  send: 'Enviar', upload: 'Enviar arquivos', download: 'Baixar arquivos',
  manage_templates: 'Gerenciar modelos', view_costs: 'Visualizar custos',
  receive_payment: 'Registrar recebimentos', view_reports: 'Visualizar relatórios',
  manage_expenses: 'Gerenciar despesas', import_nfse: 'Importar NFS-e',
  clinical_use: 'Usar IA clínica', administrative_use: 'Usar IA administrativa',
  financial_use: 'Usar IA financeira', update_clinic: 'Editar dados da clínica',
  manage_professionals: 'Gerenciar profissionais', manage_integrations: 'Gerenciar integrações',
  manage_users: 'Gerenciar usuários', manage_permissions: 'Gerenciar permissões',
};

const labelFor = (permission) => PERMISSION_LABELS[permission]
  || ACTION_LABELS[permission.split('.').slice(1).join('_')]
  || permission;

export default function PermissionOverridesModal({ member, onClose, onSave }) {
  const [catalog, setCatalog] = useState(null);
  const [modes, setModes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await api.get('/clinic-users/permissions-catalog');
        if (!active) return;
        const data = response.data || {};
        const allowed = new Set(member.permission_overrides?.allow || []);
        const denied = new Set(member.permission_overrides?.deny || []);
        setCatalog(data);
        setModes(Object.fromEntries((data.permissions || []).map((permission) => [
          permission,
          denied.has(permission) ? 'deny' : allowed.has(permission) ? 'allow' : 'inherit',
        ])));
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.detail || 'Não foi possível carregar as permissões.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [member]);

  const rolePermissions = useMemo(() => new Set(catalog?.role_permissions?.[member.role_id] || []), [catalog, member.role_id]);
  const groups = useMemo(() => GROUPS.map(([prefix, title]) => ({
    prefix,
    title,
    permissions: (catalog?.permissions || []).filter((permission) => (
      permission.startsWith(`${prefix}.`) && !HIDDEN_PERMISSIONS.has(permission)
    )),
  })).filter((group) => group.permissions.length), [catalog]);
  const customizedCount = Object.values(modes).filter((mode) => mode !== 'inherit').length;

  const save = async () => {
    const allow = Object.entries(modes).filter(([, mode]) => mode === 'allow').map(([permission]) => permission);
    const deny = Object.entries(modes).filter(([, mode]) => mode === 'deny').map(([permission]) => permission);
    try {
      setSaving(true);
      setError('');
      const saved = await onSave({ allow, deny });
      if (saved) onClose();
    } finally {
      setSaving(false);
    }
  };

  return <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-xl flex flex-col">
      <div className="p-5 border-b flex items-start justify-between gap-4">
        <div><h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-purple-600" /> Permissões personalizadas</h2><p className="text-sm text-gray-500 mt-1">{member.user?.full_name || member.user?.email} · {customizedCount} personalização(ões)</p></div>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Fechar"><X className="w-5 h-5" /></button>
      </div>
      <div className="overflow-y-auto p-5">
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 mb-5">“Padrão” acompanha o perfil atual. Use “Permitir” ou “Bloquear” somente para criar uma exceção individual.</div>
        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-5">{error}</div>}
        {loading ? <div className="py-12 text-center text-gray-500">Carregando permissões...</div> : <div className="space-y-5">{groups.map((group) => <section key={group.prefix} className="border rounded-xl overflow-hidden">
          <h3 className="px-4 py-3 bg-gray-50 border-b font-medium text-gray-900">{group.title}</h3>
          <div className="divide-y">{group.permissions.map((permission) => {
            const enabledByRole = rolePermissions.has(permission);
            return <div key={permission} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800">{labelFor(permission)}</p>{PERMISSION_DESCRIPTIONS[permission] && <p className="text-xs text-gray-500 mt-0.5">{PERMISSION_DESCRIPTIONS[permission]}</p>}<p className="text-xs text-gray-500 mt-0.5">Padrão do perfil: {enabledByRole ? 'permitido' : 'bloqueado'}</p></div>
              <select value={modes[permission] || 'inherit'} onChange={(event) => setModes((current) => ({ ...current, [permission]: event.target.value }))} className="border rounded-lg px-3 py-2 text-sm sm:w-48">
                <option value="inherit">Padrão ({enabledByRole ? 'permitido' : 'bloqueado'})</option>
                {!enabledByRole && <option value="allow">Permitir</option>}
                {enabledByRole && <option value="deny">Bloquear</option>}
              </select>
            </div>;
          })}</div>
        </section>)}</div>}
      </div>
      <div className="p-5 border-t flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <button type="button" disabled={saving || loading} onClick={() => setModes(Object.fromEntries((catalog?.permissions || []).map((permission) => [permission, 'inherit'])))} className="inline-flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"><RotateCcw className="w-4 h-4" /> Restaurar padrão do perfil</button>
        <div className="flex gap-2 sm:justify-end"><button type="button" onClick={onClose} className="flex-1 sm:flex-none px-4 py-2 border rounded-lg text-sm">Cancelar</button><button type="button" disabled={saving || loading || !!error} onClick={save} className="flex-1 sm:flex-none px-4 py-2 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar permissões'}</button></div>
      </div>
    </div>
  </div>;
}
